import React from 'react';
import Layout from '../../components/Layout';
import Icon from '../../components/AppIcon';
import { Link } from 'react-router-dom';

const TechnologyPage = () => {
  const platformStack = [
    {
      title: 'Vendor Application',
      description: 'Complete business management suite for service providers',
      icon: 'Store',
      features: [
        'Booking calendar and schedule management',
        'Payment processing and invoicing',
        'Customer relationship management',
        'Business analytics and insights',
        'Inventory and service catalog',
        'Marketing and promotion tools',
      ],
      platforms: ['iOS', 'Android', 'Web'],
    },
    {
      title: 'Customer Application',
      description: 'Service discovery and booking for end consumers',
      icon: 'Smartphone',
      features: [
        'Location-based service discovery',
        'Real-time booking and scheduling',
        'Secure payment processing',
        'Reviews and ratings system',
        'Booking history and favorites',
        'In-app messaging',
      ],
      platforms: ['iOS', 'Android'],
    },
    {
      title: 'Admin Dashboard',
      description: 'Centralized marketplace management and analytics',
      icon: 'LayoutDashboard',
      features: [
        'Real-time marketplace analytics',
        'Vendor onboarding and verification',
        'Payment reconciliation',
        'Dispute resolution tools',
        'Market expansion controls',
        'Compliance monitoring',
      ],
      platforms: ['Web'],
    },
  ];

  const coreTechnologies = [
    {
      name: 'Azure',
      category: 'Cloud Infrastructure',
      description: 'Enterprise-grade security, database services, and global CDN',
      icon: 'Cloud',
    },
    {
      name: 'Supabase',
      category: 'Real-time Database',
      description: 'PostgreSQL database with real-time subscriptions and auth',
      icon: 'Database',
    },
    {
      name: 'Stripe Connect',
      category: 'Payment Infrastructure',
      description: 'Multi-party payments, escrow, and marketplace disbursements',
      icon: 'CreditCard',
    },
    {
      name: 'AI Recommendation Engine',
      category: 'Machine Learning',
      description: 'Personalized service recommendations and search ranking',
      icon: 'Brain',
    },
    {
      name: 'Smart Search',
      category: 'Discovery Layer',
      description: 'SEO-optimized service discovery with natural language processing',
      icon: 'Search',
    },
    {
      name: 'Multi-language UI',
      category: 'Localization',
      description: 'Full i18n support with regional customization',
      icon: 'Globe',
    },
  ];

  const technicalCapabilities = [
    {
      title: 'Payments Engine',
      items: ['Stripe Connect integration', 'Multi-currency support', 'Split payments', 'Subscription billing', 'Automated payouts'],
    },
    {
      title: 'Escrow Logic',
      items: ['Secure fund holding', 'Milestone-based release', 'Dispute handling', 'Refund automation', 'Fraud prevention'],
    },
    {
      title: 'Identity & KYC',
      items: ['Document verification', 'Business validation (KYB)', 'Selfie matching', 'Sanctions screening', 'Ongoing monitoring'],
    },
    {
      title: 'Trust & Ratings',
      items: ['Verified reviews', 'Business scoring', 'Quality metrics', 'Response time tracking', 'Completion rate'],
    },
    {
      title: 'GPS & Location',
      items: ['Real-time tracking', 'Service radius logic', 'Route optimization', 'Geofencing', 'Address validation'],
    },
    {
      title: 'Offline Access',
      items: ['Offline-first architecture', 'Background sync', 'Queue management', 'Conflict resolution', 'Data persistence'],
    },
  ];

  const roadmap = [
    {
      phase: 'Current',
      items: [
        { name: 'AI-powered booking analytics', status: 'In Progress' },
        { name: 'Enhanced vendor dashboard', status: 'In Progress' },
        { name: 'Multi-language support', status: 'Complete' },
      ],
    },
    {
      phase: 'Q1 2025',
      items: [
        { name: 'Behavioral scoring system', status: 'Planned' },
        { name: 'Cash advances for vendors', status: 'Planned' },
        { name: 'SaaS dashboard for enterprise', status: 'Planned' },
      ],
    },
    {
      phase: 'Q2 2025',
      items: [
        { name: 'Loyalty and rewards engine', status: 'Planned' },
        { name: 'UE credit score', status: 'Planned' },
        { name: 'ESG reporting dashboard', status: 'Planned' },
      ],
    },
    {
      phase: 'Future',
      items: [
        { name: 'API marketplace', status: 'Research' },
        { name: 'White-label solutions', status: 'Research' },
        { name: 'Embedded finance products', status: 'Research' },
      ],
    },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-medium text-accent uppercase tracking-wider mb-4">
                Technology
              </p>
              <h1 className="text-hero text-foreground mb-6">
                Infrastructure for the Informal Economy
              </h1>
              <p className="text-body-large mb-8">
                We're not building a booking app. We're building the technological infrastructure
                that will power a trillion-dollar service economy across Latin America.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/partnerships"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-medium rounded-brand-sm hover:bg-primary-light transition-colors"
                >
                  Partner with us
                  <Icon name="ArrowRight" size={16} />
                </Link>
                <a
                  href="#stack"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-border text-foreground font-medium rounded-brand-sm hover:bg-muted transition-colors"
                >
                  View tech stack
                </a>
              </div>
            </div>

            {/* Architecture Preview */}
            <div className="bg-muted rounded-brand-lg p-8">
              <div className="text-sm font-medium text-muted-foreground mb-6 text-center">
                Platform Architecture
              </div>
              <div className="space-y-4">
                <div className="bg-white rounded-brand p-4 border border-border">
                  <div className="text-sm font-medium text-foreground mb-2">Client Layer</div>
                  <div className="flex gap-2">
                    <span className="px-2 py-1 bg-secondary/10 text-secondary text-xs rounded">iOS</span>
                    <span className="px-2 py-1 bg-secondary/10 text-secondary text-xs rounded">Android</span>
                    <span className="px-2 py-1 bg-secondary/10 text-secondary text-xs rounded">Web</span>
                  </div>
                </div>
                <div className="flex justify-center">
                  <Icon name="ArrowDown" size={20} className="text-muted-foreground" />
                </div>
                <div className="bg-white rounded-brand p-4 border border-border">
                  <div className="text-sm font-medium text-foreground mb-2">API Gateway</div>
                  <div className="flex gap-2">
                    <span className="px-2 py-1 bg-accent/10 text-accent text-xs rounded">REST</span>
                    <span className="px-2 py-1 bg-accent/10 text-accent text-xs rounded">WebSocket</span>
                    <span className="px-2 py-1 bg-accent/10 text-accent text-xs rounded">GraphQL</span>
                  </div>
                </div>
                <div className="flex justify-center">
                  <Icon name="ArrowDown" size={20} className="text-muted-foreground" />
                </div>
                <div className="bg-primary rounded-brand p-4 text-white">
                  <div className="text-sm font-medium mb-2">Core Services</div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-white/10 text-xs rounded">Payments</span>
                    <span className="px-2 py-1 bg-white/10 text-xs rounded">Booking</span>
                    <span className="px-2 py-1 bg-white/10 text-xs rounded">Identity</span>
                    <span className="px-2 py-1 bg-white/10 text-xs rounded">Analytics</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marketplace Stack */}
      <section className="py-20 bg-muted">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-display text-foreground mb-6">
              Marketplace Stack
            </h2>
            <p className="text-body-large">
              Three interconnected applications that power the entire service marketplace ecosystem.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {platformStack.map((platform, index) => (
              <div key={index} className="bg-white rounded-brand-lg p-8 border border-border">
                <div className="w-14 h-14 bg-primary/10 rounded-brand flex items-center justify-center mb-6">
                  <Icon name={platform.icon} size={28} className="text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  {platform.title}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {platform.description}
                </p>

                <div className="mb-6">
                  <div className="text-sm font-medium text-foreground mb-3">Key Features</div>
                  <ul className="space-y-2">
                    {platform.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Icon name="Check" size={14} className="text-accent mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-4 border-t border-border">
                  <div className="text-xs text-muted-foreground mb-2">Available on</div>
                  <div className="flex gap-2">
                    {platform.platforms.map((p, i) => (
                      <span key={i} className="px-2 py-1 bg-muted text-xs text-foreground rounded">
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Technologies */}
      <section id="stack" className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="mb-16">
            <h2 className="text-display text-foreground mb-6">
              Core Technologies
            </h2>
            <p className="text-body-large max-w-3xl">
              Built on enterprise-grade infrastructure trusted by the world's leading companies.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreTechnologies.map((tech, index) => (
              <div key={index} className="p-6 bg-muted rounded-brand hover:bg-white hover:shadow-corporate-md hover:border-border border border-transparent transition-all duration-300">
                <div className="w-12 h-12 bg-primary/10 rounded-brand-sm flex items-center justify-center mb-4">
                  <Icon name={tech.icon} size={24} className="text-primary" />
                </div>
                <div className="text-xs font-medium text-accent uppercase tracking-wider mb-2">
                  {tech.category}
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {tech.name}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {tech.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Capabilities */}
      <section className="py-20 bg-primary">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-display text-white mb-6">
              Technical Capabilities
            </h2>
            <p className="text-white/70 max-w-3xl mx-auto">
              Deep technical infrastructure that enables trust, commerce, and scale across the marketplace.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {technicalCapabilities.map((cap, index) => (
              <div key={index} className="bg-white/10 backdrop-blur rounded-brand p-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  {cap.title}
                </h3>
                <ul className="space-y-2">
                  {cap.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-white/80">
                      <Icon name="Check" size={14} className="text-accent-light" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="mb-16">
            <h2 className="text-display text-foreground mb-6">
              Product Roadmap
            </h2>
            <p className="text-body-large max-w-3xl">
              Our development roadmap focuses on deepening platform capabilities and expanding financial services.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {roadmap.map((phase, index) => (
              <div key={index} className="bg-muted rounded-brand p-6">
                <div className="text-sm font-medium text-accent uppercase tracking-wider mb-4">
                  {phase.phase}
                </div>
                <div className="space-y-3">
                  {phase.items.map((item, i) => (
                    <div key={i} className="flex items-start justify-between gap-2">
                      <span className="text-sm text-foreground">{item.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${
                        item.status === 'Complete' ? 'bg-accent/10 text-accent' :
                        item.status === 'In Progress' ? 'bg-secondary/10 text-secondary' :
                        item.status === 'Planned' ? 'bg-primary/10 text-primary' :
                        'bg-muted-foreground/10 text-muted-foreground'
                      }`}>
                        {item.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Infrastructure Statement */}
      <section className="py-20 bg-muted">
        <div className="max-w-narrow mx-auto px-4 lg:px-8 text-center">
          <Icon name="Layers" size={48} className="text-primary mx-auto mb-6" />
          <h2 className="text-2xl lg:text-3xl font-semibold text-foreground mb-6">
            We are not a booking app.
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            We are infrastructure for a trillion-dollar informal economy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/investor-relations"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white font-medium rounded-brand-sm hover:bg-primary-light transition-colors"
            >
              Investor Relations
              <Icon name="ArrowRight" size={16} />
            </Link>
            <Link
              to="/partnerships"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-border text-foreground font-medium rounded-brand-sm hover:bg-white transition-colors"
            >
              Partnership Opportunities
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TechnologyPage;
